<template>
  <div oncontextmenu="return false;" class="d-flex flex-column h-100">
    <b-navbar class="d-flex" style="background-color: gray">
      <div class="d-flex mx-auto" style="flex-direction: rows">
        <b-button size="sm" variant="light" @click="zoomOut" :disabled="rendering">-</b-button>
        <div
          class="d-flex mx-2 p-1"
          style="font-size: 13px; background: white; color: black; align-items: center"
        >
          <div>{{ Math.floor(scale * 100) }}%</div>
        </div>
        <b-button size="sm" variant="light" @click="zoomIn" :disabled="rendering">+</b-button>
      </div>
    </b-navbar>
    <div class="pdf-list" ref="pdf">
      <div class="pdf-contents" v-for="(n, i) in numPages" :key="i">
        <canvas :id="getCanvasId(i)"></canvas>
      </div>
    </div>
  </div>
</template>

<style scss>
.pdf-list {
  display: flex;
  width: 100%;
  flex-grow: 1;
  margin: 0 auto;
  overflow-x: auto;
  overflow-y: auto;
  flex-direction: column;
  align-items: flex-start;
  background-color: gray;
  border: 1px solid gray;
}
.pdf-contents {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-width: fit-content;
  /* コンテナ幅より狭い時は中央寄せ、ズームで広がった時はスクロール可能な左寄せ */
  margin: 0 auto;
}
canvas {
  display: flex;
  padding: 0 10px;
}
</style>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RenderedPage } from "./rendered-page";
import { getPdfDocument } from "./pdfjs-lib";
import type { PDFDocumentProxy } from "pdfjs-dist";

export default defineComponent({
  name: "pdf-viewer",
  props: {
    id: { type: String, default: "" },
    file: { type: String, default: "" },
    width: { type: String, default: "" },
    height: { type: Number, default: 0 },
  },
  setup(props) {
    // NOTE: 旧データプロパティ `pdf` は、テンプレートの ref="pdf"（template ref）と
    // setup の return 上で名前が衝突するため `pdfDoc` に改名（テンプレート未参照のため安全）。
    const pdfDoc = ref<PDFDocumentProxy | undefined>(undefined);
    const numPages = ref(0);
    const currentPage = ref(1);
    const scale = ref(1.0);
    const renderedPage = ref(new RenderedPage());

    const isFirstLoad = ref(true);
    const maxW = ref(0);

    const rendering = ref(false);

    // PDF一覧の DOM 要素（旧 this.$refs.pdf）
    const pdf = ref<HTMLElement>();

    const getCanvasId = (pageIndex: number): string => {
      return `canvas-${pageIndex + 1}-${props.id}`;
    };

    // パラメータの初期化
    const reset = async (): Promise<void> => {
      const previous = pdfDoc.value;
      pdfDoc.value = undefined;
      numPages.value = 0;
      renderedPage.value = new RenderedPage();
      isFirstLoad.value = true;
      // getDocument は PDF ごとに専用ワーカーを起こすため、destroy しないと
      // ファイル切替のたびに WASM デコーダを抱えたワーカーが残り続ける。
      await previous?.destroy();
    };

    const loadPDF = async (): Promise<void> => {
      try {
        const loaded: PDFDocumentProxy = await getPdfDocument({
          url: props.file,
        });

        pdfDoc.value = loaded;
        numPages.value = loaded.numPages;
      } catch (e) {
        // 元の例外を残さないと原因調査ができないため、握りつぶさずログに出す。
        // S3 presigned URL の署名クエリが Sentry breadcrumb 経由で外部送信されない
        // よう、クエリ文字列は落とす。
        console.error("loadPDF error: file =", props.file.split("?")[0], "cause =", e);
        pdfDoc.value = undefined;
        throw "loadPDF error";
      }
    };

    const renderPage = async (pageIndex: number): Promise<void> => {
      const page = await pdfDoc.value?.getPage(pageIndex + 1);
      if (!page || renderedPage.value.exists(page)) {
        return;
      }

      if (isFirstLoad.value) {
        // 最初表示のみ(拡大縮小押すまで): scaleを幅の約95%に合わせて画面いっぱいに近づける
        const origin = page.getViewport({ scale: 1.0 });
        scale.value = (maxW.value * 0.95) / origin.width;
      }
      const viewport = page.getViewport({ scale: scale.value });

      const canvas = assureCanvas(pageIndex);

      // 表示サイズ（CSS px）とバッキングストア（実ピクセル）を分離する。
      // 低倍率で this.scale をそのまま canvas.width に使うとラスタライズ解像度まで
      // 縮小されてしまい、無駄に解像感が落ちるため、devicePixelRatio に加えて
      // 低倍率時には内部解像度の下駄を履かせる。
      const dpr = window.devicePixelRatio || 1;
      const outputScale = Math.max(dpr, dpr / Math.max(scale.value, 0.1));

      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const ctx = canvas.getContext("2d");
      if (ctx === null) return;

      const transform: [number, number, number, number, number, number] | undefined =
        outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

      await page.render({
        canvas,
        canvasContext: ctx,
        viewport: viewport,
        transform,
      }).promise;
      renderedPage.value.add(page);
    };

    const _renderPages = async (): Promise<void> => {
      // レンダリング中はZoomIn,ZoomOutボタンをdisabledへ
      rendering.value = true;
      const first = Promise.resolve();
      let before = first;
      for (let i = 0; i < numPages.value; i++) {
        before = before.then(() => renderPage(i));
      }
      before
        .catch((e: unknown) => {
          console.error("An error occurred during rendering.", e);
        })
        .finally(() => {
          rendering.value = false;
        });
    };

    const renderPages = async (): Promise<void> => {
      _renderPages().catch((e) => {
        console.log("_renderPages error:", e);
        rendering.value = false;
      });
    };

    // PDFのロード+レンダリング
    const loadAndRender = async (): Promise<void> => {
      const el = pdf.value;
      if (el) maxW.value = el.offsetWidth;
      try {
        await reset();
        // ストリームビューアのファイル切替時は path クリアで file が一旦空になる。
        // 空のまま getDocument を呼ぶと「expected either data, range, or url」で
        // 落ちるため、次の file 解決（watch 再発火）までロードを保留する。
        if (!props.file) return;
        await loadPDF();
        await renderPages();
      } catch (e) {
        console.log("load error ", e);
      }
    };

    watch(
      () => props.file,
      async (): Promise<void> => {
        await loadAndRender();
      }
    );

    const assureCanvas = (pageIndex: number): HTMLCanvasElement => {
      const canvas = document.getElementById(getCanvasId(pageIndex));
      if (canvas instanceof HTMLCanvasElement) {
        return canvas;
      }
      const newCanvas = document.createElement("canvas");
      newCanvas.setAttribute("id", getCanvasId(pageIndex));
      const pageDiv = document.getElementById("page-" + (pageIndex + 1));
      if (pageDiv) {
        pageDiv.appendChild(newCanvas);
      }
      return newCanvas;
    };

    const updateScale = (newScale: number): void => {
      scale.value = newScale;
      renderedPage.value.clear();
      renderPages();
    };

    const zoomIn = (): void => {
      if (isFirstLoad.value) {
        // (n * 0.25)の近似値にscale変換
        const nearScale = Math.round(scale.value / 0.25) * 0.25;
        scale.value = nearScale;
        isFirstLoad.value = false;
      }
      updateScale(Math.min(scale.value + 0.25, 4.0));
    };

    const zoomOut = (): void => {
      if (isFirstLoad.value) {
        // (n * 0.25)の近似値にscale変換
        const nearScale = Math.round(scale.value / 0.25) * 0.25;
        scale.value = nearScale;
        isFirstLoad.value = false;
      }
      updateScale(Math.max(scale.value - 0.25, 0.1));
    };

    onMounted(async (): Promise<void> => {
      await loadAndRender();
    });

    onBeforeUnmount(async (): Promise<void> => {
      try {
        await pdfDoc.value?.destroy();
      } catch (e) {
        console.log("destroy error ", e);
      }
    });

    return {
      pdfDoc,
      numPages,
      currentPage,
      scale,
      renderedPage,
      isFirstLoad,
      maxW,
      rendering,
      pdf,
      getCanvasId,
      loadAndRender,
      reset,
      loadPDF,
      renderPages,
      renderPage,
      assureCanvas,
      updateScale,
      zoomIn,
      zoomOut,
    };
  },
});
</script>
